import React, { FunctionComponent, useState } from 'react'
import styles from './DesignStatusPicker.module.sass'
import { Task } from '../../../responses/tasks'
import UserPicker from '../../data-components/pickers/UserPicker/UserPicker'
import { TenderStatus, tenderToColor } from '../../data-components/pickers/TenderPicker/TenderPicker'
import DesignIcon from '../../../../files/design-status/design.svg'
import ProductionIcon from '../../../../files/design-status/production.svg'
import Radio from '../../controls/Radio/Radio'
import { identity } from 'fp-ts/es6/function'
import clsx from 'clsx'
import Results from '../../data-components/Results/Results'
import { useRootDispatch } from '../../../utils/hooks'
import { isDefined } from '../../../utils/utils'
import { galleryActions } from '../../../redux/global/gallery/gallery-actions'
import { enumMatchAll } from '../../../utils/enum-utils'
import TaskTab from '../../navigation/TaskTabs/TaskTab'
import { Colors } from '../../../styles'
import { User } from '../../../responses/users'
import { DesignStatus } from '../../../responses/objects'

const tenders = [TenderStatus.WON, TenderStatus.LOST, TenderStatus.CANCELED]
const statuses = [DesignStatus.WON, DesignStatus.LOST, DesignStatus.CANCELED]

const tenderToName = enumMatchAll<TenderStatus>()({
    [TenderStatus.CANCELED]: 'Отмена',
    [TenderStatus.IN_WORK]: 'В работу',
    [TenderStatus.WON]: 'Выигрыш',
    [TenderStatus.LOST]: 'Проигрыш'
})

const statusToName = enumMatchAll<DesignStatus>()({
    [DesignStatus.NONE]: 'Без статуса',
    [DesignStatus.WON]: 'Выигрыш',
    [DesignStatus.LOST]: 'Проигрыш',
    [DesignStatus.CANCELED]: 'Отмена',
})

const statusToColor = enumMatchAll<DesignStatus>()({
    [DesignStatus.NONE]: Colors.grey,
    [DesignStatus.WON]: Colors.green,
    [DesignStatus.LOST]: Colors.red,
    [DesignStatus.CANCELED]: Colors.violet,
})

export const isTaskIncomplete = (task: Task): boolean => task.tender_id === TenderStatus.IN_WORK
    || (task.tender_id !== TenderStatus.CANCELED && !task.tendered_user)

type Props = {
    type: 'tender'
    task: Task,
    onChange: (task: Task) => void,
} | {
    type: 'design' | 'production',
    status: DesignStatus,
    onChange: (status: DesignStatus) => void,
}

const DesignStatusPicker: FunctionComponent<Props> = props => {
    const dispatch = useRootDispatch()
    const warning = props.type === 'tender' ? isTaskIncomplete(props.task) : props.status === DesignStatus.NONE
    const isDesign = props.type === 'design' || props.type === 'tender'

    const [users, setUsers] = useState<User[]>([])

    return <div className={clsx(styles.picker, { [styles.warning]: warning })}>
        {
            props.type === 'tender' && <Results
                className={styles.results}
                mode={'task'}
                taskId={props.task.id}
                onClick={current => dispatch(galleryActions.open({
                    type: 'task',
                    current,
                    tasksId: [props.task.id],
                    objectId: props.task.project_work_object_id,
                }))}
            />
        }
        <div className={styles.text}>
            <div className={styles.title}>
                {
                    isDesign ?
                        `Дизайн${props.type === 'tender' ? '-тендер' : ''}` :
                        'Производство'
                }
            </div>
            <div className={styles.description}>Статус объекта по {isDesign ? 'дизайну' : 'производству'}</div>
        </div>
        {
            props.type === 'tender' && <>
                <TaskTab className={styles.type} task={props.task}/>
                {
                    (props.task.tender_id === TenderStatus.WON || props.task.tender_id === TenderStatus.LOST) &&
                    <UserPicker
                        className={styles.user}
                        data={users}
                        warning={!props.task.tendered_user}
                        user={props.task.tendered_user}
                        text={props.task.tender_id === TenderStatus.WON ? 'Победитель' : 'Проигравший'}
                        title={props.task.tender_id === TenderStatus.WON ? 'Изменить победителя' : 'Изменить проигравшего'}
                        onChange={search => setUsers([props.task.target_user, ...props.task.users]
                            .filter(isDefined).filter(user => user.name.toLowerCase().includes(search.toLowerCase()))
                        )}
                        onClick={tendered_user => props.onChange({ ...props.task, tendered_user })}
                    />
                }
            </>
        }
        {
            isDesign? <DesignIcon className={styles.icon}/>: <ProductionIcon className={styles.icon}/>
        }
        {
            props.type === 'tender' ? <Radio
                className={styles.tender}
                value={props.task.tender_id}
                items={tenders}
                toKey={identity}
                render={tenderToName}
                toColor={tenderToColor}
                onChange={tender_id => props.onChange({ ...props.task, tender_id })}
            /> : <Radio
                className={styles.tender}
                value={props.status}
                items={statuses}
                toKey={identity}
                render={statusToName}
                toColor={statusToColor}
                onChange={props.onChange}
            />
        }
    </div>
}

export default DesignStatusPicker